import React, { useMemo } from 'react';
import { Card, CardBody } from 'reactstrap';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from 'recharts';
import { AuthActivityProps, TSorObj } from '../../../../types/Dashboard';
import AjaxSourcedData from '../../../formsAndTable/tables/dataTables/dataSource/AjaxSourcedData';
import { getAuthUserTableColumns } from '../../../../data/formsAndTable/tables/dataTables/DataSource';
import { useTranslation } from 'react-i18next';
const pad = (n: number) => String(n).padStart(2, '0');
const dateKey = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const toLocalDateTime = (iso: string, locale = 'uk-UA') => {
  const d = new Date(iso);
  const date = new Intl.DateTimeFormat(locale, {
    timeZone: 'Europe/Kyiv',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(d); // e.g. "21.08.2025"

  const time = new Intl.DateTimeFormat(locale, {
    timeZone: 'Europe/Kyiv',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  }).format(d); // e.g. "14:06:24"

  return { date, time, tsNum: d.getTime() };
};

const normalize = (list: TSorObj[], type: 'login' | 'logout') =>
  (list ?? []).map((item) => {
    if (typeof item === 'string') {
      return {
        type,
        ts: item,
        device_id: null as string | null,
        session_id: null as string | null,
      };
    }
    return {
      type,
      ts: item.ts,
      device_id: item.device_id ?? null,
      session_id: item.session_id ?? null,
    };
  });

const AuthActivity: React.FC<AuthActivityProps> = ({ auth, title = 'Auth activity' }) => {
  const { t } = useTranslation();
  const events = useMemo(() => {
    const rows = [...normalize(auth.logins_ts, 'login'), ...normalize(auth.logouts_ts, 'logout')];
    return rows
      .map((r) => ({ ...r, ...toLocalDateTime(r.ts, 'uk-UA') }))
      .sort((a, b) => (a.ts < b.ts ? -1 : a.ts > b.ts ? 1 : 0));
  }, [auth]);

  const daily = useMemo(() => {
    const map = new Map<string, { date: string; logins: number; logouts: number }>();
    for (const e of events) {
      const key = e.date;
      if (!map.has(key)) map.set(key, { date: key, logins: 0, logouts: 0 });
      const row = map.get(key)!;
      if (e.type === 'login') row.logins += 1;
      else row.logouts += 1;
    }
    return Array.from(map.values()).sort((a, b) => (a.date < b.date ? -1 : 1));
  }, [events]);

  return (
    <Card className="mt-3">
      <CardBody>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 16, marginBottom: 8 }}>
          <h6 style={{ margin: 0 }}>{title}</h6>
          <div style={{ color: '#6b7280', fontSize: 13 }}>
            {t('stats.charts.logins')}: <b>{auth.logins}</b> · {t('stats.charts.logouts')}:{' '}
            <b>{auth.logouts}</b>
          </div>
        </div>

        {/* Daily stacked bars
        <div style={{ width: '100%', height: 240 }}>
          {daily.length ? (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={daily} margin={{ left: 8, right: 16, top: 10, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                <XAxis dataKey="date" />
                <YAxis allowDecimals={false} />
                <Tooltip />
                <Legend />
                <Bar dataKey="logins" name="Logins" />
                <Bar dataKey="logouts" name="Logouts" />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div style={{ color: '#9ca3af' }}>No auth events in range</div>
          )}
        </div> */}

        {/* Detailed table */}
        <div className="table-responsive mt-3">
          <AjaxSourcedData data={events} columns={getAuthUserTableColumns(t)} />
        </div>
      </CardBody>
    </Card>
  );
};

export default AuthActivity;
